import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { PlankSymbol } from "./Logo";
import { useStore } from "../context/StoreContext";
import { inr, itemKey } from "../lib/api";

export const QtyStepper = ({ qty, onChange, testid }) => (
  <div className="inline-flex items-center rounded-full border border-border">
    <button data-testid={`${testid}-dec`} onClick={() => onChange(qty - 1)} className="grid h-8 w-8 place-items-center" aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
    <span data-testid={`${testid}-value`} className="w-6 text-center text-sm font-semibold">{qty}</span>
    <button data-testid={`${testid}-inc`} onClick={() => onChange(qty + 1)} className="grid h-8 w-8 place-items-center" aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
  </div>
);

const CartLine = ({ item }) => {
  const { updateQty, removeFromCart, setCartOpen } = useStore();
  const key = itemKey(item);
  const tid = key.replace(":", "-");
  return (
    <li data-testid={`cart-item-${tid}`} className="flex gap-4 py-5">
      <img src={item.image} alt={item.name} className="h-20 w-20 rounded object-cover" />
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex justify-between gap-2">
          <div>
            <Link to={`/product/${item.slug}${item.variant ? `?finish=${item.variant}` : ""}`} onClick={() => setCartOpen(false)} className="font-display text-lg leading-tight">{item.name}</Link>
            {item.variant_name && <p data-testid={`cart-variant-${tid}`} className="text-xs text-muted-foreground">Finish: {item.variant_name}</p>}
          </div>
          <button data-testid={`cart-remove-${tid}`} onClick={() => removeFromCart(key)} aria-label="Remove" className="text-muted-foreground hover:text-sheesham"><Trash2 className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center justify-between">
          <QtyStepper qty={item.qty} onChange={(q) => updateQty(key, q)} testid={`cart-qty-${tid}`} />
          <div className="text-right">
            <p className="font-semibold">{inr(item.price * item.qty)}</p>
            {item.mrp > item.price && <p className="text-xs text-muted-foreground line-through">{inr(item.mrp * item.qty)}</p>}
            {item.gst_exclusive && <p className="text-xs text-muted-foreground">+ {item.gst_rate}% GST</p>}
          </div>
        </div>
      </div>
    </li>
  );
};

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, subtotal, mrpTotal, count, taxBase } = useStore();
  const navigate = useNavigate();
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col bg-background p-0 sm:max-w-md">
        <div className="border-b border-border px-6 py-5">
          <SheetTitle className="font-display text-2xl font-normal">Your cart ({count})</SheetTitle>
        </div>
        {cart.length === 0 ? (
          <div data-testid="cart-empty" className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <PlankSymbol className="h-14 w-14 text-foreground" />
            <p className="font-display text-xl">Nothing in the box yet.</p>
            <p className="text-sm text-muted-foreground">Start with one rack. Add a shelf anytime.</p>
            <button data-testid="cart-empty-shop-button" onClick={() => { setCartOpen(false); navigate("/product/pustak-rack"); }} className="btn-sheesham">Shop the Pustak Rack</button>
          </div>
        ) : (
          <>
            <ul className="flex-1 divide-y divide-border overflow-y-auto px-6">
              {cart.map((i) => <CartLine key={itemKey(i)} item={i} />)}
            </ul>
            <div className="space-y-3 border-t border-border bg-card px-6 py-5">
              <p className="flex items-center gap-2 text-sm text-neem dark:text-jute"><Truck className="h-4 w-4" /> Free delivery across Hyderabad in 1 to 2 days</p>
              {mrpTotal > subtotal && <div className="flex justify-between text-sm"><span className="text-muted-foreground">You save vs MRP</span><span data-testid="cart-savings" className="font-semibold">{inr(mrpTotal - subtotal)}</span></div>}
              <div className="flex justify-between text-lg"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-semibold">{inr(subtotal)}</span></div>
              <button data-testid="cart-checkout-button" onClick={() => { setCartOpen(false); navigate("/checkout"); }} className="btn-sheesham w-full py-3.5">Checkout</button>
              <p className="text-center text-xs text-muted-foreground">{taxBase > 0 ? `GST of about ${inr(Math.round(taxBase))} is added at checkout for pieces priced excluding tax.` : "Prices include GST."} Apply coupons at checkout.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
